<template>
    <div>
        <h2 class="title" :class="marginClass">{{ $t('surgery.surgeryData') }}</h2>
        <hr class="divider">
        <date-field
            v-model="hospitalization_date"
            :class="marginClass"
            :field="surgeryFields.hospitalization_date"
            :errors="fieldErrors"
            field-name="hospitalization_date"
            prefix-field-name="hospitalization"
            datetime
        />
        <date-field
            v-model="date"
            :class="marginClass"
            :field="surgeryFields.date"
            :errors="fieldErrors"
            field-name="date"
            datetime
        />
        <select-as-radio-field
            v-model="anesthesia_type"
            :field="surgeryFields.anesthesia_type"
            :errors="fieldErrors"
            :class="marginClass"
            field-name="anesthesia_type"
        />
        <input-field
            v-model="weight"
            :field="surgeryFields.weight"
            :errors="fieldErrors"
            :form-width="3"
            :class="marginClass"
            field-name="weight"
            type="number"
            show-help-as-unit
        />
        <date-field
            v-if="isCreating"
            :class="marginClass"
            :field="novoeightTreatmentFields.date"
            v-model="total_dose_date"
            field-name="total_dose_date"
            prefix-field-name="total_dose_date"
            :label="$t('surgery.preSurgeryDoseDate')"
            :errors="fieldErrors"
            :inline="false"
            datetime
        />
        <input-field
            v-if="isCreating"
            :class="marginClass"
            :field="novoeightTreatmentFields.total_dose"
            :label="$t('surgery.preSurgeryDose')"
            v-model="total_dose"
            field-name="total_dose"
            :errors="fieldErrors"
            show-help-as-unit
        >
            <template v-slot:extralabel>
                <div class="ml-1 d-inline-block">
                    <i
                        v-b-modal.modal-asistencial-protocol
                        class="fas fa-question-circle clickable text-primary"
                    />
                    <b-modal
                        id="modal-asistencial-protocol"
                        size="lg"
                        :title="$t('surgery.doseHelp.title')"
                        ok-only
                    >
                        <assistance-protocol-table />
                    </b-modal>
                </div>
            </template>

            <template v-slot:unit>
                {{ novoeightTreatmentFields.total_dose.help_text }} |
                {{ calculateDosePerKg(total_dose, weight) | numberOrEmpty }}
                {{ $t('utils.FVIIPerKgUnit') }}
            </template>
        </input-field>
        <select-as-radio-field
            :class="marginClass"
            :field="surgeryFields.clinic_response"
            v-model="clinic_response"
            field-name="clinic_response"
            :errors="fieldErrors"
        >
            <div class="d-inline-block">
                <i
                    v-b-modal.modal-efficiency
                    class="fas fa-question-circle clickable text-primary"
                />

                <b-modal
                    id="modal-efficiency"
                    size="lg"
                    :title="$t('surgery.efficiencyTable.title')"
                    ok-only
                >
                    <efficiency-table />
                </b-modal>
            </div>
        </select-as-radio-field>
    </div>
</template>

<script>
    import InputField from "@/components/fields/InputField";
    import {mapState} from "vuex";
    import {mapStateProperty} from "@/utils/VuexHelpers";
    import SurgeryAddElementMixin from "@/components/mixins/NavigableComponentMixin";
    import DateField from "@/components/fields/DateField";
    import AssistanceProtocolTable from "@/components/surgery/AssistanceProtocolTable";
    import {required} from "vuelidate/lib/validators";
    import EfficiencyTable from "@/components/surgery/EfficiencyTable";
    import SelectAsRadioField from "@/components/fields/SelectAsRadioField";
    import {isNull} from "@/utils/formatting";
    import requiredIf from "vuelidate/lib/validators/requiredIf";
    import {calculateDosePerKg, MAJOR_SURGERY} from "@/components/surgery/SurgeryUtils";

    export default {
        name: "SurgeryData",
        components: {
            SelectAsRadioField,
            EfficiencyTable,
            AssistanceProtocolTable,
            DateField,
            InputField,
        },
        mixins: [SurgeryAddElementMixin],
        props: {
            surgeryType: {
                type: String,
            },
        },
        computed: {
            ...mapState([
                'surgery',
                'surgeryFields',
                'novoeightTreatmentFields',
            ]),
            ...mapStateProperty([
                'surgery.date',
                'surgery.anesthesia_type',
                'surgery.weight',
                'surgery.hospitalization_date',
                {path: 'surgery.novoeight_treatment.total_dose', name: 'total_dose'},
                {path: 'surgery.novoeight_treatment.date', name: 'total_dose_date'},
                'surgery.clinic_response',
            ]),
            marginClass() {
                return this.isCreating ? 'mx-4 mb-3' : 'ml-3 mb-3';
            },
            fieldErrors() {
                let errors = {};
                if (this.$v.date.$error) {
                    errors['date'] = [this.$t('error.required')];
                }
                if (this.$v.weight.$error) {
                    errors['weight'] = [this.$t('error.required')];
                }
                if (this.$v.total_dose.$error) {
                    errors['total_dose'] = [this.$t('error.required')];
                }
                if (this.$v.total_dose_date.$error) {
                    errors['total_dose_date'] = [this.$t('error.required')];
                }
                if (this.$v.anesthesia_type.$error) {
                    errors['anesthesia_type'] = [this.$t('error.required')];
                }
                if (this.$v.hospitalization_date.$error) {
                    errors['hospitalization_date'] = [this.$t('error.required')];
                }
                return errors;
            },
            isCreating() {
                return isNull(this.surgery.post_surgery_days);
            }
        },
        methods: {
            validate() {
                this.$v.$touch();
                return !this.$v.$invalid;
            },
            back() {
            },
            calculateDosePerKg,
        },
        validations: {
            date: {
                required,
            },
            weight: {
                required,
            },
            total_dose: {
                required: requiredIf(function () {
                    return this.isCreating;
                }),
            },
            total_dose_date: {
                required: requiredIf(function () {
                    return this.isCreating;
                }),
            },
            anesthesia_type: {
                required,
            },
            hospitalization_date: {
                required: requiredIf(function () {
                    return this.surgeryType === MAJOR_SURGERY;
                })
            },
        }
    };
</script>

<style scoped>
</style>